import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ChevronLeft, Zap, AlertCircle, QrCode, Building2, Loader2,
  Copy, Clock, CheckCircle2, RefreshCw, Phone, Gamepad2, ShoppingBag, Wallet,
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import theme from '../theme.config';
import { PaymentBadges } from '../components/PaymentBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { id: 'qris', name: 'QRIS', icon: QrCode, desc: 'GoPay, OVO, Dana, dll' },
  { id: 'va_bni', name: 'VA BNI', icon: Building2, desc: 'Transfer via BNI', channel: 'bni' },
  { id: 'va_bri', name: 'VA BRI', icon: Building2, desc: 'Transfer via BRI', channel: 'bri' },
  { id: 'va_mandiri', name: 'VA Mandiri', icon: Building2, desc: 'Transfer via Mandiri', channel: 'mandiri' },
  { id: 'va_cimb', name: 'VA CIMB', icon: Building2, desc: 'Transfer via CIMB', channel: 'cimb' },
];

const BANK_INFO = {
  bni: { name: 'BNI', color: '#f15a22' },
  bri: { name: 'BRI', color: '#00529c' },
  mandiri: { name: 'Mandiri', color: '#003366' },
  cimb: { name: 'CIMB', color: '#ec1c24' },
};

// Default fallback config
const DEFAULT_CFG = {
  type: 'game_id',
  id_label: 'User ID',
  id_placeholder: 'Masukkan User ID',
  id_required: true,
  show_id2: false,
  instruction: 'Masukkan data akun Anda.',
  success_label: 'User ID',
};

export default function DigiFlazzProduct() {
  const { brandSlug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState('games');
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [loading, setLoading] = useState(true);
  const [selectedSku, setSelectedSku] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [userId, setUserId] = useState('');
  const [serverId, setServerId] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);

  const [paymentData, setPaymentData] = useState(null);
  const [paymentStep, setPaymentStep] = useState('form');
  const [orderStatus, setOrderStatus] = useState(null);

  useEffect(() => { fetchBrand(); }, [brandSlug]);
  useEffect(() => { if (user?.email) setEmail(user.email); }, [user]);

  // Poll payment status
  useEffect(() => {
    if (paymentStep !== 'paying' || !paymentData?.orderId) return;
    let active = true;
    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/payment/status/${paymentData.orderId}`);
        if (!active) return;
        setOrderStatus(res.data);
        if (res.data.status === 'paid' || res.data.status === 'completed') {
          setPaymentStep('success');
          return;
        }
        if (res.data.status === 'failed' || res.data.status === 'cancelled') {
          setPaymentStep('failed');
          return;
        }
      } catch {}
      if (active) setTimeout(poll, 4000);
    };
    setTimeout(poll, 4000);
    return () => { active = false; };
  }, [paymentStep, paymentData?.orderId]);

  // Keep polling on success for SN
  useEffect(() => {
    if (paymentStep !== 'success' || !paymentData?.orderId) return;
    if (orderStatus?.digiflazz_sn && orderStatus?.status === 'completed') return;
    let active = true;
    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/payment/status/${paymentData.orderId}`);
        if (!active) return;
        setOrderStatus(res.data);
        if (res.data.digiflazz_sn && res.data.status === 'completed') return;
      } catch {}
      if (active) setTimeout(poll, 5000);
    };
    setTimeout(poll, 3000);
    return () => { active = false; };
  }, [paymentStep, paymentData?.orderId, orderStatus?.digiflazz_sn]);

  const fetchBrand = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/biller/catalog/${brandSlug}`);
      setBrand(res.data);
      setProducts(res.data.products || []);
      setCategory(res.data.category || 'games');
      if (res.data.input_config) setCfg(res.data.input_config);
    } catch {
      toast.error('Produk tidak ditemukan');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSku) return toast.error('Pilih item terlebih dahulu');
    if (!selectedPayment) return toast.error('Pilih metode pembayaran');
    if (cfg.id_required && !userId) return toast.error(`Masukkan ${cfg.id_label}`);
    if (!email) return toast.error('Masukkan email');

    setSubmitting(true);
    try {
      const orderRes = await axios.post(`${API_URL}/api/orders/digiflazz`, {
        brand: brand.brand,
        sku_code: selectedSku.sku,
        product_name: selectedSku.name,
        game_user_id: userId,
        game_server_id: serverId || null,
        email,
        payment_method: selectedPayment.id,
        price: selectedSku.price,
      });
      const orderId = orderRes.data.order.id;

      const isQris = selectedPayment.id === 'qris';
      const payRes = await axios.post(`${API_URL}/api/payment/create`, {
        order_id: orderId,
        amount: selectedSku.price,
        customer_name: email.split('@')[0],
        customer_email: email,
        payment_method: isQris ? 'qris' : 'va',
        va_channel: selectedPayment.channel || 'bni',
        item_name: `${brand?.brand} - ${selectedSku.name}`,
        digiflazz_sku: selectedSku.sku,
        customer_game_id: cfg.show_id2 && serverId ? `${userId}${serverId}` : userId,
      });

      if (payRes.data.success) {
        setPaymentData({ ...payRes.data.data, orderId });
        setPaymentStep('paying');
        toast.success('Payment berhasil dibuat!');
      } else {
        toast.error(payRes.data.message || 'Gagal membuat payment');
      }
    } catch (err) {
      console.error(err);
      toast.error('Gagal memproses pesanan');
    } finally {
      setSubmitting(false);
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Disalin!');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!brand) return null;

  // ==================== FAILED / CANCELLED ====================
  if (paymentStep === 'failed') {
    return (
      <div className="min-h-screen pt-24 pb-12 cyber-grid">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6 neon-magenta">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-red-500/30 bg-red-500/5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            <span className="font-mono text-[10px] text-red-400 tracking-widest uppercase">TRANSMISSION_FAILED</span>
          </div>
          <h1 className="font-bold text-2xl text-white uppercase mb-2 tracking-wider">
            {orderStatus?.status === 'cancelled' ? 'Transmisi Dibatalkan' : 'Transmisi Gagal'}
          </h1>
          <p className="text-muted-foreground text-sm font-mono mb-8">
            {orderStatus?.status === 'cancelled'
              ? 'Transmisi energi dibatalkan atau waktu QRIS telah kedaluwarsa. Silakan coba kembali.'
              : 'Terjadi kegagalan komunikasi satelit pada gateway pembayaran. Silakan coba kembali.'}
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" className="border-border text-white hover:bg-white/5 font-mono text-xs"
              onClick={() => { setPaymentStep('form'); setPaymentData(null); setOrderStatus(null); }}>
              <RefreshCw className="w-4 h-4 mr-2" /> [ COBA LAGI ]
            </Button>
            <Button className="bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest neon-green text-xs" onClick={() => navigate('/')}>
              Kembali ke Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== SUCCESS ====================
  if (paymentStep === 'success') {
    const isVoucher = category === 'voucher';
    const hasSn = orderStatus?.digiflazz_sn;
    const topupDone = orderStatus?.topup_status === 'success';
    const topupFailed = orderStatus?.topup_status === 'failed';
    const isProcessing = !hasSn && !topupFailed;

    return (
      <div className="min-h-screen pt-24 pb-12 cyber-grid">
        <div className="max-w-lg mx-auto px-4 text-center">

          {/* Header - changes based on state */}
          {isProcessing ? (
            <>
              <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6 neon-magenta">
                <Loader2 className="w-10 h-10 text-yellow-500 animate-spin" />
              </div>
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-yellow-500/30 bg-yellow-500/5">
                <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />
                <span className="font-mono text-[10px] text-yellow-400 tracking-widest uppercase">CARGO_DISPATCHING</span>
              </div>
              <h1 className="font-bold text-2xl text-white uppercase mb-2 tracking-wider">Memproses Kargo...</h1>
              <p className="text-muted-foreground text-sm font-mono mb-4">
                Komunikasi energi berhasil! Sedang merutekan {isVoucher ? 'kode voucher' : category === 'pulsa' ? 'pengiriman pulsa' : 'top-up'} ke orbit tujuan.
              </p>
              <div className="bg-yellow-500/5 border border-yellow-500/30 rounded-xl p-4 mb-6 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-yellow-500" />
                <div className="flex items-center justify-center gap-2 text-yellow-400 text-xs font-mono font-bold mb-1">
                  <AlertCircle className="w-4 h-4" />
                  JANGAN TUTUP TRANSMISI (STAY_ONLINE)
                </div>
                <p className="text-[10px] text-yellow-400/70 font-mono">
                  {isVoucher ? 'Modul enkripsi voucher sedang memecahkan kode pada manifest ini.' :
                   category === 'pulsa' ? 'Menunggu konfirmasi status node pulsa.' :
                   'Manifest top-up digital sedang disinkronisasikan ke game database.'}
                </p>
              </div>

              {/* Processing animation */}
              <div className="flex justify-center gap-1.5 mb-6">
                {[0,1,2,3,4].map(i => (
                  <div key={i} className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" style={{ animationDelay: `${i * 0.2}s` }} />
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6 neon-green">
                <CheckCircle2 className="w-10 h-10 text-primary" />
              </div>
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-primary/30 bg-primary/5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                <span className="font-mono text-[10px] text-primary tracking-widest uppercase">NODE_RESOLVED</span>
              </div>
              <h1 className="font-bold text-2xl text-white uppercase mb-2 tracking-wider">
                {topupFailed ? 'Kargo Tertunda' : 'Transmisi Selesai!'}
              </h1>
              <p className="text-muted-foreground text-sm font-mono mb-6">
                {topupFailed ? 'Pembayaran terverifikasi namun routing otomatis terganggu. Hubungi Admin.' :
                 `Kargo digital telah berhasil didistribusikan melalui jalur konduit AetherCore.`}
              </p>
            </>
          )}

          {/* SN / Voucher Code */}
          {hasSn && (
            <div className="bg-primary/5 border-2 border-primary/40 rounded-xl p-5 mb-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-primary/70" />
              <p className="text-[10px] text-primary mb-2 uppercase tracking-widest font-mono">
                {isVoucher ? 'DECRYPTED_VOUCHER_KEY' : category === 'pulsa' ? 'SERIAL_NUMBER' : 'SN_TRANSACTION_TOKEN'}
              </p>
              <div className="flex items-center justify-center gap-3">
                <span className="font-mono text-white font-bold text-lg break-all">{orderStatus.digiflazz_sn}</span>
                <button onClick={() => copyText(orderStatus.digiflazz_sn)}
                  className="p-2 rounded-lg bg-primary/20 text-primary hover:bg-primary/30 flex-shrink-0">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              {isVoucher && (
                <p className="text-[10px] text-muted-foreground mt-3 font-mono">Gunakan modul penukaran platform eksternal untuk redeem.</p>
              )}
            </div>
          )}

          {/* Top-up failed warning */}
          {topupFailed && (
            <div className="bg-red-500/5 border border-red-500/30 rounded-xl p-4 mb-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-red-500" />
              <p className="text-red-400 text-xs font-bold font-mono">COMMUNICATION_ERROR</p>
              {orderStatus?.topup_error && (
                <p className="text-[10px] text-red-400/70 mt-1 font-mono">{orderStatus.topup_error}</p>
              )}
              <p className="text-[10px] text-muted-foreground mt-2 font-mono">Silakan hubungi command center admin untuk pemrosesan manual.</p>
            </div>
          )}

          {/* Order details */}
          <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card mb-6 text-left relative overflow-hidden">
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/50" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-secondary/50" />
            <div className="flex items-center gap-3 mb-4">
              <img src={brand?.image} alt={brand?.brand} className="w-12 h-12 rounded-lg object-cover border border-border" />
              <div>
                <p className="font-bold text-white text-xs uppercase tracking-wide">{brand?.brand}</p>
                <p className="text-xs text-primary font-mono">{selectedSku?.name}</p>
              </div>
            </div>
            <div className="space-y-3 border-t border-border/40 pt-4">
              <div className="flex justify-between text-xs font-mono">
                <span className="text-muted-foreground uppercase">TARGET_ID</span>
                <span className="text-white font-bold">{userId}{cfg.show_id2 && serverId ? ` (${serverId})` : ''}</span>
              </div>
              <div className="flex justify-between text-xs font-mono">
                <span className="text-muted-foreground uppercase">GATEWAY_STATUS</span>
                <span className="text-primary font-bold">SUCCESS</span>
              </div>
              <div className="flex justify-between text-xs font-mono">
                <span className="text-muted-foreground uppercase">ROUTING_STATUS</span>
                <span className={`font-bold ${topupDone ? 'text-primary' : topupFailed ? 'text-red-500' : 'text-yellow-500 animate-pulse'}`}>
                  {topupDone ? 'SUCCESS' : topupFailed ? 'FAILED' : 'ROUTING...'}
                </span>
              </div>
            </div>
            <div className="border-t border-border/40 mt-4 pt-4 flex justify-between items-center">
              <span className="text-muted-foreground text-xs font-mono uppercase">TOTAL ENERGI</span>
              <span className="font-mono text-lg font-bold text-primary text-glow-green">Rp {selectedSku?.price?.toLocaleString('id-ID')}</span>
            </div>
          </div>

          {/* Buttons - only show when processing is done */}
          {!isProcessing && (
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 border-border text-white hover:bg-white/5 font-mono text-xs" onClick={() => navigate('/track')}>[ Cek Transaksi ]</Button>
              <Button className="flex-1 bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest neon-green text-xs" onClick={() => navigate('/')}>Kembali ke Home</Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==================== PAYING ====================
  if (paymentStep === 'paying' && paymentData) {
    const isVA = paymentData.payment_method === 'virtual_account';
    return (
      <div className="min-h-screen pt-24 pb-12 cyber-grid">
        <div className="max-w-lg mx-auto px-4">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4 neon-magenta animate-pulse">
              <Clock className="w-8 h-8 text-secondary" />
            </div>
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-secondary/30 bg-secondary/5">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
              <span className="font-mono text-[10px] text-secondary tracking-widest uppercase">TRANSMISSION_PENDING</span>
            </div>
            <h1 className="font-bold text-2xl text-white uppercase mb-2 tracking-wider">Menunggu Saluran</h1>
            <p className="text-muted-foreground text-sm font-mono">Selesaikan pengiriman energi sebelum status node kedaluwarsa (24 jam)</p>
          </div>

          <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card mb-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/50" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-secondary/50" />
            <div className="flex items-center gap-3 mb-4">
              <img src={brand.image} alt={brand.brand} className="w-12 h-12 rounded-lg object-cover border border-border" />
              <div>
                <p className="font-bold text-white text-xs uppercase tracking-wide">{brand.brand}</p>
                <p className="text-xs text-primary font-mono">{selectedSku?.name}</p>
              </div>
            </div>
            <div className="text-xs font-mono text-muted-foreground mb-3 uppercase">
              {cfg.success_label.toUpperCase()}: <span className="text-white font-bold">{userId}{cfg.show_id2 && serverId ? ` (${serverId})` : ''}</span>
            </div>
            <div className="border-t border-border/40 pt-4 flex justify-between items-center">
              <span className="text-muted-foreground text-xs font-mono uppercase">TOTAL MANIFEST</span>
              <span className="font-mono text-lg font-bold text-primary text-glow-green">Rp {selectedSku?.price?.toLocaleString('id-ID')}</span>
            </div>
          </div>

          {isVA ? (
            <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card mb-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/50" />
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1.5 rounded font-bold text-xs font-mono text-white"
                  style={{ backgroundColor: BANK_INFO[paymentData.channel]?.color || '#333' }}>
                  {BANK_INFO[paymentData.channel]?.name || paymentData.channel?.toUpperCase()}
                </span>
                <span className="text-white font-mono text-xs uppercase tracking-widest">Virtual Account Node</span>
              </div>
              <div className="bg-black/40 rounded-lg p-4 mb-4 border border-border/50">
                <p className="text-[10px] font-mono text-muted-foreground mb-2">REK_NODE_VIRTUAL</p>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-lg text-white font-bold tracking-wider">{paymentData.va_number}</span>
                  <button onClick={() => copyText(paymentData.va_number)} className="p-2 rounded-lg bg-primary/20 text-primary hover:bg-primary/30 transition-colors">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground font-mono">Transfer ke stasiun VA di atas melalui ATM atau mobile banking terminal Anda.</p>
            </div>
          ) : (
            <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card mb-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-secondary/50" />
              <div className="flex items-center gap-3 mb-4">
                <QrCode className="w-6 h-6 text-secondary animate-pulse" />
                <span className="font-bold text-white text-xs font-mono">ORBITAL QRIS SCREEN</span>
              </div>
              <div className="bg-white rounded-xl p-6 mb-4 flex justify-center border border-border/50">
                {paymentData.qr_content ? (
                  <QRCodeCanvas value={paymentData.qr_content} size={220} level="M" marginSize={2} />
                ) : (
                  <div className="w-[220px] h-[220px] flex items-center justify-center">
                    <QrCode className="w-32 h-32 text-gray-300" />
                  </div>
                )}
              </div>
              <p className="text-center text-xs text-muted-foreground font-mono">Scan QR code dengan e-wallet atau mobile banking terminal Anda</p>
            </div>
          )}

          <Button variant="outline" className="w-full border-border text-muted-foreground hover:text-white font-mono text-xs uppercase"
            onClick={() => { setPaymentStep('form'); setPaymentData(null); }}>
            <RefreshCw className="w-4 h-4 mr-2" /> Ubah Jalur Energi
          </Button>
          <p className="text-center text-xs text-muted-foreground mt-4 flex items-center justify-center gap-2 font-mono uppercase">
            <Loader2 className="w-3 h-3 animate-spin text-secondary" /> Menghubungi node gateway pembayaran...
          </p>
        </div>
      </div>
    );
  }

  // ==================== FORM ====================

  return (
    <div className="min-h-screen pt-16 cyber-grid">
      {/* Banner */}
      <div className="relative h-48 md:h-56 overflow-hidden border-b border-border/50">
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8 max-w-7xl mx-auto">
          <Button variant="ghost" size="sm" className="text-white hover:text-primary mb-4 font-mono text-xs" onClick={() => navigate('/')}>
            <ChevronLeft className="w-4 h-4 mr-1" /> [ KEMBALI ]
          </Button>
          <div className="flex items-center gap-4">
            <img src={brand.image} alt={brand.brand} className="w-16 h-16 md:w-20 md:h-20 rounded-xl object-cover border-2 border-primary/50 neon-green" />
            <div>
              <h1 className="font-bold text-2xl md:text-3xl text-white uppercase tracking-wider">{brand.brand}</h1>
              <p className="text-primary font-mono text-xs uppercase tracking-widest mt-1">NODE // {products.length} item tersedia</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {/* Column 1: Target Identification & Logs */}
          <div className="space-y-6">
            {/* Instructions */}
            <div className="bg-secondary/5 border border-secondary/30 rounded-xl p-4 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-secondary" />
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-300 font-mono">{cfg.instruction}</p>
              </div>
            </div>

            {/* ID inputs - adapted per category */}
            <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/50 rounded-tl-xl" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-secondary/50 rounded-br-xl" />
              <h2 className="font-mono font-bold text-sm text-white uppercase mb-4 tracking-wider flex items-center gap-2">
                {cfg.type === 'phone' ? <Phone className="w-4 h-4 text-primary" /> :
                 cfg.type === 'voucher_code' ? <ShoppingBag className="w-4 h-4 text-primary" /> :
                 <Gamepad2 className="w-4 h-4 text-primary" />}
                [ 01 · {cfg.type === 'voucher_code' ? 'INFORMASI TARGET' : 'DATA TUJUAN'} ]
              </h2>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label className="font-mono text-xs text-muted-foreground uppercase tracking-widest">▸ {cfg.id_label}</Label>
                  <Input placeholder={cfg.id_placeholder} className="bg-black/50 border-border text-white font-mono focus:border-primary focus:ring-1 focus:ring-primary/30"
                    value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="df-user-id" />
                </div>
                {cfg.show_id2 && (
                  <div className="space-y-2">
                    <Label className="font-mono text-xs text-muted-foreground uppercase tracking-widest">▸ {cfg.id2_label}</Label>
                    <Input placeholder={cfg.id2_placeholder} className="bg-black/50 border-border text-white font-mono focus:border-primary focus:ring-1 focus:ring-primary/30"
                      value={serverId} onChange={(e) => setServerId(e.target.value)} data-testid="df-server-id" />
                  </div>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/50 rounded-tl-xl" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-secondary/50 rounded-br-xl" />
              <h2 className="font-mono font-bold text-sm text-white uppercase mb-4 tracking-wider">[ 02 · EMAIL NOTIFIKASI ]</h2>
              <div className="space-y-2">
                <Label className="font-mono text-xs text-muted-foreground uppercase tracking-widest">▸ Email</Label>
                <Input type="email" placeholder="Masukkan email" className="bg-black/50 border-border text-white font-mono focus:border-primary focus:ring-1 focus:ring-primary/30"
                  value={email} onChange={(e) => setEmail(e.target.value)} data-testid="df-email" />
              </div>
            </div>

            {/* Item selection — moved from col 2 */}
            <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/50 rounded-tl-xl" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-secondary/50 rounded-br-xl" />
              <h2 className="font-mono font-bold text-sm text-white uppercase mb-4 tracking-wider">[ 03 · PILIH ITEM ]</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {products.map((p) => (
                  <button key={p.sku}
                    className={`denom-card p-4 rounded-xl text-left border border-border/50 transition-all ${selectedSku?.sku === p.sku ? 'selected border-primary bg-primary/10' : 'bg-black/30 hover:border-primary/50'}`}
                    onClick={() => setSelectedSku(p)} data-testid={`sku-${p.sku}`}>
                    <p className="font-mono font-bold text-white text-xs uppercase tracking-wider">{p.name}</p>
                    <p className="font-mono text-sm text-primary mt-1 font-bold">Rp {p.price.toLocaleString('id-ID')}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Column 2: Payment Channels only */}
          <div className="space-y-6">
            {/* Payment */}
            <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/50 rounded-tl-xl" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-secondary/50 rounded-br-xl" />
              <h2 className="font-mono font-bold text-sm text-white uppercase mb-4 tracking-wider">[ 04 · JALUR TRANSMISI ]</h2>
              <div className="grid grid-cols-1 gap-3">
                {PAYMENT_METHODS.map((m) => {
                  const Icon = m.icon;
                  return (
                    <button key={m.id}
                      className={`payment-card p-4 rounded-xl flex items-center gap-3 border border-border/50 transition-all ${selectedPayment?.id === m.id ? 'selected border-secondary bg-secondary/10' : 'bg-black/30 hover:border-secondary/50'}`}
                      onClick={() => setSelectedPayment(m)} data-testid={`pay-${m.id}`}>
                      <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 border border-border/40">
                        <Icon className="w-5 h-5 text-secondary" />
                      </div>
                      <div className="text-left font-mono">
                        <p className="font-bold text-white text-xs uppercase tracking-wide">{m.name}</p>
                        <p className="text-[10px] text-muted-foreground">{m.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Column 3: Manifest, Indicators & Trigger */}
          <div className="sticky top-24 space-y-6">
            <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/50 rounded-tl-xl" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-secondary/50 rounded-br-xl" />
              <h2 className="font-mono font-bold text-sm text-white uppercase mb-4 tracking-wider">[ MANIFEST STEAM ]</h2>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 border-b border-border/40 pb-3">
                  <img src={brand.image} alt={brand.brand} className="w-12 h-12 rounded-lg object-cover border border-border" />
                  <div>
                    <p className="font-bold text-white text-xs uppercase tracking-wide">{brand.brand}</p>
                    {selectedSku && <p className="text-xs text-primary font-mono">{selectedSku.name}</p>}
                  </div>
                </div>
                {userId && (
                  <div className="bg-black/40 rounded-lg p-3 font-mono text-xs border border-border/50">
                    <p className="text-gray-300">{cfg.id_label.toUpperCase()}: <span className="text-white font-bold">{userId}</span></p>
                    {cfg.show_id2 && serverId && <p className="text-gray-300">{cfg.id2_label.toUpperCase()}: <span className="text-white font-bold">{serverId}</span></p>}
                  </div>
                )}
                {selectedPayment && (
                  <div className="flex items-center gap-2 text-xs font-mono text-gray-300">
                    <Wallet className="w-4 h-4 text-secondary" />
                    CHANNEL: {selectedPayment.name.toUpperCase()}
                  </div>
                )}
              </div>

              {/* Decorative mini steampunk telemetry widget */}
              <div className="p-3 bg-black/40 border border-primary/20 rounded-lg font-mono text-[10px] space-y-2 mb-6">
                <div className="flex justify-between items-center text-muted-foreground">
                  <span>REACTOR_PRESSURE:</span>
                  <span className="text-primary animate-pulse">12.4 BAR</span>
                </div>
                <div className="w-full bg-black/60 rounded-full h-1.5 overflow-hidden">
                  <div className="bg-primary h-full animate-pulse" style={{ width: '75%' }} />
                </div>
                <div className="flex justify-between text-[8px] text-muted-foreground">
                  <span>CONDUIT_TEMP: 240°C</span>
                  <span>FLOW: OK</span>
                </div>
              </div>

              <div className="border-t border-border pt-4 mb-6 flex justify-between items-center">
                <span className="font-mono text-xs text-muted-foreground uppercase">TOTAL</span>
                <span className="font-mono text-lg font-bold text-white text-glow-green">
                  {selectedSku ? `Rp ${selectedSku.price.toLocaleString('id-ID')}` : 'Rp 0'}
                </span>
              </div>
              <Button className="w-full bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest py-6 neon-green text-sm"
                onClick={handleSubmit} disabled={submitting || !selectedSku || !selectedPayment}
                data-testid="df-order-btn">
                {submitting ? (
                  <span className="flex items-center gap-2 font-mono"><Loader2 className="w-4 h-4 animate-spin" /> PROCESSING...</span>
                ) : (
                  <span className="flex items-center gap-2 font-mono"><Zap className="w-4 h-4" /> [ TRANSAKSI SEKARANG ]</span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
